import React, { useState, useEffect } from 'react';
import { 
  X, Check, ChevronRight, ChevronLeft, 
  ExternalLink, Smartphone, Shield, Key, 
  Loader2, AlertCircle
} from 'lucide-react';

export interface TwilioSetupData {
  twilioSid: string;
  twilioToken: string;
  twilioPhone: string;
}

export interface AISetupData {
  aiProvider: 'openai' | 'anthropic' | 'google' | 'together' | 'deepseek' | 'groq' | 'minimax' | 'mistral';
  apiKey: string;
}

interface SetupWizardProps {
  type: 'twilio' | 'ai';
  onClose: () => void;
  onComplete: (data: TwilioSetupData | AISetupData) => void;
}

const AI_PROVIDER_LINKS: Record<string, string> = {
  google: 'https://aistudio.google.com/app/apikey',
  anthropic: 'https://console.anthropic.com/settings/keys',
  openai: 'https://platform.openai.com/api-keys',
  deepseek: 'https://platform.deepseek.com/api_keys',
  groq: 'https://console.groq.com/keys',
  minimax: 'https://platform.minimaxi.com/user-center/basic-information/interface-key',
  mistral: 'https://console.mistral.ai/api-keys/',
  together: 'https://api.together.ai/settings/api-keys',
};

const AI_PROVIDERS = [
  { id: 'google', name: 'Gemini', icon: '✨' },
  { id: 'anthropic', name: 'Claude', icon: '🧠' },
  { id: 'openai', name: 'OpenAI', icon: '🤖' },
  { id: 'deepseek', name: 'DeepSeek', icon: '🔍' },
  { id: 'groq', name: 'Groq', icon: '⚡' },
  { id: 'minimax', name: 'Minimax', icon: '🪐' },
  { id: 'mistral', name: 'Mistral', icon: '🌪️' },
  { id: 'together', name: 'Together', icon: '🚀' }
];

export const SetupWizard: React.FC<SetupWizardProps> = ({ type, onClose, onComplete }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Twilio Data
  const [twilioSid, setTwilioSid] = useState('');
  const [twilioToken, setTwilioToken] = useState('');
  const [twilioPhone, setTwilioPhone] = useState('');

  // AI Data
  const [aiProvider, setAiProvider] = useState<AISetupData['aiProvider']>('google');
  const [apiKey, setApiKey] = useState('');

  const totalSteps = type === 'twilio' ? 3 : 2;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleNext = async () => {
    if (step === totalSteps) {
      if (type === 'twilio') {
        onComplete({ twilioSid, twilioToken, twilioPhone });
      } else {
        onComplete({ aiProvider, apiKey });
      }
      onClose();
      return;
    }

    if (type === 'twilio' && step === 2) {
       // Validate connection
       setLoading(true);
       setError(null);
       try {
         // Mock validation or actual test call
         setTimeout(() => {
           setLoading(false);
           setStep(step + 1);
         }, 1000);
       } catch (err) {
         setError('Failed to validate credentials. Please check and try again.');
         setLoading(false);
       }
       return;
    }

    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(Math.max(1, step - 1));
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div className="w-full max-w-lg bg-[var(--t-surface)] border border-[var(--t-border)] rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[var(--t-border)] flex items-center justify-between bg-[var(--t-background)]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[var(--t-primary-dim)] flex items-center justify-center">
              {type === 'twilio' ? <Smartphone className="w-4 h-4 text-[var(--t-primary)]" /> : <Shield className="w-4 h-4 text-[var(--t-primary)]" />}
            </div>
            <div>
              <h2 className="text-sm font-bold text-white uppercase tracking-wider">
                {type === 'twilio' ? 'Twilio Setup Wizard' : 'AI Provider Setup'}
              </h2>
              <p className="text-[10px] text-[var(--t-text-muted)]">Step {step} of {totalSteps}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--t-surface-hover)] rounded-full transition-colors">
            <X className="w-4 h-4 text-[var(--t-text-muted)]" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-[var(--t-background)] w-full">
          <div 
            className="h-full bg-[var(--t-primary)] transition-all duration-500" 
            style={{ width: `${(step / totalSteps) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-8">
          {type === 'twilio' && (
            <div className="space-y-6">
              {step === 1 && (
                <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
                  <div className="p-4 rounded-2xl bg-blue-500/10 border border-blue-500/20">
                    <p className="text-xs text-blue-200 leading-relaxed">
                      Twilio allows you to send professional SMS messages from your own dedicated business number.
                    </p>
                    <a 
                      href="https://www.twilio.com/console" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-blue-400 hover:underline"
                    >
                      Get your credentials from Twilio Console <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-[var(--t-text-muted)] uppercase tracking-widest">Account SID</label>
                      <input 
                        type="text" 
                        value={twilioSid}
                        onChange={(e) => setTwilioSid(e.target.value)}
                        placeholder="AC..."
                        className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-[var(--t-primary-dim)] transition-all"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-[var(--t-text-muted)] uppercase tracking-widest">Auth Token</label>
                      <input 
                        type="password" 
                        value={twilioToken}
                        onChange={(e) => setTwilioToken(e.target.value)}
                        placeholder="Your Twilio Auth Token"
                        className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-[var(--t-primary-dim)] transition-all"
                      />
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
                  <div className="text-center space-y-2">
                    <div className="w-12 h-12 rounded-full bg-[var(--t-primary-dim)] flex items-center justify-center mx-auto mb-4">
                      <Smartphone className="w-6 h-6 text-[var(--t-primary)]" />
                    </div>
                    <h3 className="text-sm font-bold text-white">Add Your Phone Number</h3>
                    <p className="text-xs text-[var(--t-text-muted)]">Enter the Twilio phone number you want to use.</p>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-[var(--t-text-muted)] uppercase tracking-widest">Phone Number</label>
                    <input 
                      type="text" 
                      value={twilioPhone}
                      onChange={(e) => setTwilioPhone(e.target.value)}
                      placeholder="+1234567890"
                      className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-[var(--t-primary-dim)] transition-all"
                    />
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-6 text-center animate-in zoom-in-95 duration-300">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
                    <Check className="w-8 h-8 text-green-500" />
                  </div>
                  <div>
                    <h3 className="text-base font-black text-white uppercase tracking-tight">Setup Complete!</h3>
                    <p className="text-xs text-[var(--t-text-muted)] mt-2">
                      Your Twilio connection is ready to go. You can now send and receive SMS messages directly through the inbox.
                    </p>
                  </div>
                  <div className="p-4 rounded-2xl bg-[var(--t-background)] border border-[var(--t-border)] text-left">
                    <div className="flex justify-between text-[10px] py-1 border-b border-[var(--t-border)]">
                      <span className="text-[var(--t-text-muted)]">Account SID</span>
                      <span className="text-white font-mono">{twilioSid.substring(0, 8)}...</span>
                    </div>
                    <div className="flex justify-between text-[10px] py-1">
                      <span className="text-[var(--t-text-muted)]">Phone</span>
                      <span className="text-white font-mono">{twilioPhone}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {type === 'ai' && (
            <div className="space-y-6">
              {step === 1 && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {AI_PROVIDERS.map((provider) => (
                      <button
                        key={provider.id}
                        onClick={() => setAiProvider(provider.id as any)}
                        className={`p-3 rounded-xl border transition-all text-center group flex flex-col items-center justify-center gap-2 ${
                          aiProvider === provider.id 
                            ? 'bg-[var(--t-primary-dim)] border-[var(--t-primary)] shadow-[0_0_15px_rgba(var(--t-primary-rgb),0.3)]' 
                            : 'bg-[var(--t-background)] border-[var(--t-border)] hover:border-[var(--t-text-muted)]'
                        }`}
                      >
                        <span className="text-xl">{provider.icon}</span>
                        <span className={`text-[10px] font-black uppercase tracking-tighter truncate w-full ${aiProvider === provider.id ? 'text-white' : 'text-[var(--t-text-muted)] group-hover:text-white'}`}>
                          {provider.name}
                        </span>
                      </button>
                    ))}
                  </div>

                  <div className="p-4 rounded-2xl bg-blue-500/10 border border-blue-500/20 space-y-2">
                    <p className="text-[11px] text-blue-200 leading-relaxed font-medium">
                      Setup Instructions: Click the link below to go to the {aiProvider} dashboard, generate a new API key, and paste it in the field below.
                    </p>
                    <a 
                      href={AI_PROVIDER_LINKS[aiProvider]}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[10px] font-black text-blue-400 hover:text-blue-300 transition-colors uppercase tracking-widest"
                    >
                      Get {aiProvider} API Key <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>

                  <div className="space-y-1 mt-4">
                    <label className="text-[10px] font-bold text-[var(--t-text-muted)] uppercase tracking-widest">API Key</label>
                    <div className="relative">
                      <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--t-text-muted)]" />
                      <input 
                        type="password" 
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder={`Paste your ${aiProvider} API key here`}
                        className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl pl-11 pr-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-[var(--t-primary-dim)] transition-all font-mono"
                      />
                    </div>
                  </div>
                </>
              )}

              {step === 2 && (
                <div className="space-y-6 text-center animate-in zoom-in-95 duration-300">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
                    <Check className="w-8 h-8 text-green-500" />
                  </div>
                  <div>
                    <h3 className="text-base font-black text-white uppercase tracking-tight">AI Connected!</h3>
                    <p className="text-xs text-[var(--t-text-muted)] mt-2">
                      Your {aiProvider} API key has been securely saved. The AI can now help you draft responses and automate lead follow-ups.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center gap-2 text-red-400 text-xs">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-6 border-t border-[var(--t-border)] bg-[var(--t-background)] flex items-center justify-between">
          <button
            onClick={handleBack}
            disabled={step === 1 || loading}
            className={`flex items-center gap-1 text-xs font-bold uppercase tracking-widest transition-all ${
              step === 1 ? 'opacity-0 pointer-events-none' : 'text-[var(--t-text-muted)] hover:text-white'
            }`}
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>

          <button
            onClick={handleNext}
            disabled={loading || (type === 'twilio' && step === 1 && (!twilioSid || !twilioToken)) || (type === 'ai' && step === 1 && !apiKey)}
            className="flex items-center gap-2 px-8 py-3 bg-[var(--t-primary)] text-black text-xs font-black uppercase tracking-widest rounded-xl hover:scale-105 active:scale-95 transition-all shadow-lg disabled:opacity-50 disabled:scale-100"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                {step === totalSteps ? 'Finish' : 'Continue'}
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
